import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import AdminLayout from '@/components/Layout';
import { Film, Tv2, Plus, Trash2, Grid3x3, List, X, Calendar, Clock, Plus as PlusIcon, Pencil, Save, Loader2 } from 'lucide-react';

const TABS = [
  { id: 'movies', label: 'Filmler',  icon: Film, type: 'vod' },
  { id: 'series', label: 'Diziler',  icon: Tv2,  type: 'series' },
];

const EMPTY_MOVIE  = { title: '', category_id: '', stream_url: '', poster_url: '', year: '', tmdb_id: '', duration_min: '', description: '' };
const EMPTY_SERIES = { title: '', category_id: '', poster_url: '', year: '', tmdb_id: '', description: '', episodes: [] };

export default function Vod() {
  const [tab, setTab] = useState('movies');
  const [view, setView] = useState('grid');
  const [movies, setMovies] = useState([]);
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);   // {kind: 'movies'|'series', data: {...}}

  const load = async () => {
    setLoading(true);
    try {
      const [m, s, c] = await Promise.all([
        api.get('/movies'),
        api.get('/series'),
        api.get('/categories'),
      ]);
      setMovies(m.data);
      setSeries(s.data);
      setCategories(c.data);
    } catch (err) {
      console.error('VOD veri yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const currentTab = TABS.find(t => t.id === tab);
  const items = tab === 'movies' ? movies : series;

  // Veri tutarlılığı: kategorileri yalnızca aktif tab'ın type'ına göre filtrele
  const tabCategories = useMemo(
    () => categories.filter(c => c.type === currentTab.type),
    [categories, currentTab.type]
  );

  const categoryName = (id) => categories.find(c => c._id === id)?.name || 'Kategorisiz';

  const handleDelete = async (id) => {
    const endpoint = tab === 'movies' ? 'movies' : 'series';
    const what = tab === 'movies' ? 'Bu filmi' : 'Bu diziyi';
    if (!window.confirm(`${what} silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/${endpoint}/${id}`);
      if (tab === 'movies') setMovies(movies.filter(m => m._id !== id));
      else setSeries(series.filter(s => s._id !== id));
      toast.success(tab === 'movies' ? 'Film silindi' : 'Dizi silindi');
    } catch {
      toast.error('Silinemedi.');
    }
  };

  const openEdit = (item) => {
    setEditItem({ kind: tab, data: { ...item } });
  };
  const onEditSaved = (updated) => {
    if (editItem.kind === 'movies') {
      setMovies(prev => prev.map(m => (m._id === updated._id ? { ...m, ...updated } : m)));
    } else {
      setSeries(prev => prev.map(s => (s._id === updated._id ? { ...s, ...updated } : s)));
    }
    setEditItem(null);
    toast.success('Değişiklikler kaydedildi');
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-testid="vod-page-title">
            {tab === 'movies' ? <Film className="text-amber-500" /> : <Tv2 className="text-amber-500" />}
            VOD & Dizi Kütüphanesi
          </h1>
          <button
            onClick={() => setShowAdd(true)}
            className="bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-transform active:scale-95"
            data-testid="vod-add-btn"
          >
            <Plus size={20} /> {tab === 'movies' ? 'Film Ekle' : 'Dizi Ekle'}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="inline-flex bg-slate-900 border border-slate-800 rounded-xl p-1">
            {TABS.map(t => {
              const Icon = t.icon;
              const isOn = tab === t.id;
              const count = t.id === 'movies' ? movies.length : series.length;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition ${isOn ? 'bg-amber-500 text-black shadow-lg shadow-amber-500/20' : 'text-gray-400 hover:text-white'}`}
                  data-testid={`vod-tab-${t.id}`}
                >
                  <Icon size={16} /> {t.label}
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${isOn ? 'bg-black/20 text-black' : 'bg-slate-800 text-gray-500'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* View toggle */}
          <div className="inline-flex bg-slate-900 border border-slate-800 rounded-xl p-1">
            <button
              onClick={() => setView('grid')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition ${view === 'grid' ? 'bg-slate-800 text-amber-400' : 'text-gray-500 hover:text-white'}`}
              data-testid="vod-view-grid"
              title="Poster Grid"
            >
              <Grid3x3 size={14} /> Grid
            </button>
            <button
              onClick={() => setView('list')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition ${view === 'list' ? 'bg-slate-800 text-amber-400' : 'text-gray-500 hover:text-white'}`}
              data-testid="vod-view-list"
              title="Liste"
            >
              <List size={14} /> Liste
            </button>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-12 text-center text-gray-500 italic">
            Yükleniyor…
          </div>
        ) : items.length === 0 ? (
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-12 text-center text-gray-500" data-testid="vod-empty">
            <div className="italic mb-3">{tab === 'movies' ? 'Henüz film eklenmemiş.' : 'Henüz dizi eklenmemiş.'}</div>
            {tabCategories.length === 0 && (
              <div className="text-[11px] text-amber-700/70">
                Önce <span className="text-amber-500">/admin/categories</span> sayfasından
                <span className="text-amber-500 font-bold"> type: {currentTab.type}</span> tipinde bir kategori ekleyin.
              </div>
            )}
          </div>
        ) : view === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-testid="vod-grid">
            {items.map(it => (
              <PosterCard
                key={it._id}
                item={it}
                kind={tab}
                categoryName={categoryName(it.category_id)}
                onDelete={() => handleDelete(it._id)}
                onEdit={() => openEdit(it)}
              />
            ))}
          </div>
        ) : (
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
            <table className="w-full text-left text-white" data-testid="vod-table">
              <thead className="bg-slate-950 text-gray-400 text-xs uppercase font-bold">
                <tr>
                  <th className="p-4 w-16">Poster</th>
                  <th className="p-4">Başlık</th>
                  <th className="p-4">Kategori</th>
                  <th className="p-4">Yıl</th>
                  {tab === 'movies' ? <th className="p-4">Süre</th> : <th className="p-4">Bölüm</th>}
                  <th className="p-4">TMDB</th>
                  <th className="p-4 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {items.map(it => (
                  <tr key={it._id} className="hover:bg-slate-800/30 transition-colors" data-testid={`vod-row-${it._id}`}>
                    <td className="p-3">
                      <div className="w-10 h-14 bg-slate-950 border border-slate-800 rounded overflow-hidden">
                        {it.poster_url
                          ? <img src={it.poster_url} alt="" className="w-full h-full object-cover" />
                          : <div className="w-full h-full flex items-center justify-center text-slate-700">{tab === 'movies' ? <Film size={16} /> : <Tv2 size={16} />}</div>}
                      </div>
                    </td>
                    <td className="p-4 font-bold text-white">{it.title}</td>
                    <td className="p-4">
                      <span className="text-xs px-2 py-0.5 bg-slate-800 text-slate-300 rounded-full border border-slate-700">{categoryName(it.category_id)}</span>
                    </td>
                    <td className="p-4 text-gray-400 font-mono text-sm">{it.year || '—'}</td>
                    {tab === 'movies'
                      ? <td className="p-4 text-gray-400 font-mono text-sm">{it.duration_min ? `${it.duration_min} dk` : '—'}</td>
                      : <td className="p-4 text-gray-400 font-mono text-sm">{(it.episodes?.length || 0)} bölüm</td>}
                    <td className="p-4 text-gray-500 font-mono text-xs">{it.tmdb_id || '—'}</td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => openEdit(it)}
                          className="text-gray-500 hover:text-amber-400 hover:bg-amber-500/10 p-2 rounded-lg transition-all"
                          data-testid={`vod-edit-btn-${it._id}`}
                          title="Düzenle"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(it._id)}
                          className="text-gray-500 hover:text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-all"
                          data-testid={`vod-delete-btn-${it._id}`}
                          title="Sil"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showAdd && (
          tab === 'movies'
            ? <AddMovieModal categories={tabCategories} onClose={() => setShowAdd(false)} onCreated={load} />
            : <AddSeriesModal categories={tabCategories} onClose={() => setShowAdd(false)} onCreated={load} />
        )}

        {editItem && (
          <EditMediaModal
            kind={editItem.kind}
            data={editItem.data}
            categories={categories.filter(c => c.type === (editItem.kind === 'movies' ? 'vod' : 'series'))}
            onClose={() => setEditItem(null)}
            onSaved={onEditSaved}
          />
        )}
      </div>
    </AdminLayout>
  );
}

/* ---------------- Poster Card ---------------- */

function PosterCard({ item, kind, categoryName, onDelete, onEdit }) {
  return (
    <div className="group relative bg-slate-900 border border-slate-800 rounded-xl overflow-hidden hover:border-amber-500/40 transition" data-testid={`vod-card-${item._id}`}>
      <div className="aspect-[2/3] bg-slate-950 overflow-hidden">
        {item.poster_url ? (
          <img
            src={item.poster_url}
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-700 gap-2">
            {kind === 'movies' ? <Film size={32} /> : <Tv2 size={32} />}
            <span className="text-[10px] font-mono uppercase">Poster yok</span>
          </div>
        )}
      </div>

      <div className="p-3">
        <div className="text-sm font-bold text-white truncate" title={item.title}>{item.title}</div>
        <div className="flex items-center gap-2 mt-1 text-[10px] font-mono text-gray-500">
          {item.year && <span className="flex items-center gap-1"><Calendar size={10} /> {item.year}</span>}
          {kind === 'movies' && item.duration_min && (
            <span className="flex items-center gap-1"><Clock size={10} /> {item.duration_min}dk</span>
          )}
          {kind === 'series' && (
            <span className="flex items-center gap-1"><PlusIcon size={10} /> {item.episodes?.length || 0} bölüm</span>
          )}
        </div>
        <div className="mt-1.5 text-[10px] truncate text-amber-500/80 uppercase tracking-wider font-bold">{categoryName}</div>
      </div>

      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
        <button
          onClick={onEdit}
          className="bg-amber-500/90 hover:bg-amber-500 text-black p-1.5 rounded-lg shadow-lg"
          data-testid={`vod-card-edit-${item._id}`}
          title="Düzenle"
          type="button"
        >
          <Pencil size={14} />
        </button>
        <button
          onClick={onDelete}
          className="bg-red-500/90 hover:bg-red-500 text-white p-1.5 rounded-lg shadow-lg"
          data-testid={`vod-card-delete-${item._id}`}
          title="Sil"
          type="button"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

/* ---------------- Add Movie Modal ---------------- */

function AddMovieModal({ categories, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_MOVIE);
  const [saving, setSaving] = useState(false);
  const [tmdbBusy, setTmdbBusy] = useState(false);
  const [tmdbErr, setTmdbErr] = useState('');

  const fetchFromTmdb = async () => {
    if (!form.tmdb_id) { setTmdbErr('Önce TMDB ID girin.'); return; }
    setTmdbErr('');
    setTmdbBusy(true);
    try {
      const res = await api.get(`/tmdb/movie/${encodeURIComponent(form.tmdb_id)}`);
      const d = res.data;
      setForm(f => ({
        ...f,
        title:        f.title || d.title || '',
        year:         d.year ? String(d.year) : f.year,
        duration_min: d.duration_min ? String(d.duration_min) : f.duration_min,
        description:  d.description || f.description,
        poster_url:   d.poster_url || f.poster_url,
      }));
    } catch (err) {
      setTmdbErr(err?.response?.data?.detail || 'TMDB lookup başarısız.');
    } finally {
      setTmdbBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/movies', {
        ...form,
        year: form.year ? parseInt(form.year) : null,
        duration_min: form.duration_min ? parseInt(form.duration_min) : null,
      });
      onCreated();
      onClose();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Film eklenemedi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100] p-4" data-testid="vod-add-modal">
      <div className="bg-slate-900 border border-slate-800 w-full max-w-2xl rounded-2xl p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-white flex items-center gap-2">
            <Film className="text-amber-500" /> Yeni Film
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        {categories.length === 0 ? (
          <div className="bg-amber-500/5 border border-amber-700/30 rounded-xl p-5 text-sm text-amber-300/80">
            <strong className="text-amber-400 block mb-1">Kategori yok</strong>
            Film eklemek için önce <span className="text-amber-500">/admin/categories</span> sayfasından
            <span className="font-bold"> type: vod</span> tipinde bir kategori oluşturun.
          </div>
        ) : (
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-500 uppercase ml-1">Film Adı</label>
              <input required placeholder="Örn: The Matrix" className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none"
                value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="movie-title-input" />
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 uppercase ml-1">Kategori</label>
              <select required className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none cursor-pointer"
                value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} data-testid="movie-category-select">
                <option value="">Seçiniz...</option>
                {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 uppercase ml-1">Yıl</label>
              <input type="number" min="1900" max="2099" placeholder="1999" className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono"
                value={form.year} onChange={e => setForm({ ...form, year: e.target.value })} data-testid="movie-year-input" />
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 uppercase ml-1">Süre (dk)</label>
              <input type="number" min="1" placeholder="120" className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono"
                value={form.duration_min} onChange={e => setForm({ ...form, duration_min: e.target.value })} data-testid="movie-duration-input" />
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 uppercase ml-1">TMDB ID</label>
              <div className="mt-1 flex gap-2">
                <input
                  placeholder="603"
                  className="flex-1 bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono"
                  value={form.tmdb_id}
                  onChange={e => setForm({ ...form, tmdb_id: e.target.value })}
                  data-testid="movie-tmdb-input"
                />
                <button
                  type="button"
                  onClick={fetchFromTmdb}
                  disabled={tmdbBusy || !form.tmdb_id}
                  className="bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-400 px-3 rounded-xl font-bold text-xs uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1"
                  data-testid="movie-tmdb-fetch-btn"
                  title="TMDB'den poster, açıklama ve yıl bilgilerini getir"
                >
                  {tmdbBusy ? '⏳' : '↓'} TMDB
                </button>
              </div>
              {tmdbErr && <div className="text-[10px] text-red-400 mt-1 ml-1" data-testid="movie-tmdb-err">{tmdbErr}</div>}
            </div>

            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-500 uppercase ml-1">Stream URL</label>
              <input required placeholder="http://cdn.example.com/movies/matrix.mp4" className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono"
                value={form.stream_url} onChange={e => setForm({ ...form, stream_url: e.target.value })} data-testid="movie-stream-input" />
            </div>

            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-500 uppercase ml-1">Poster URL</label>
              <input placeholder="https://image.tmdb.org/..." className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono"
                value={form.poster_url} onChange={e => setForm({ ...form, poster_url: e.target.value })} data-testid="movie-poster-input" />
            </div>

            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-500 uppercase ml-1">Açıklama (Opsiyonel)</label>
              <textarea rows="3" placeholder="Kısa film açıklaması..." className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none resize-none"
                value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>

            <div className="flex gap-4 pt-4 md:col-span-2">
              <button type="button" onClick={onClose} className="flex-1 bg-slate-800 text-white font-bold py-4 rounded-xl hover:bg-slate-700 transition-colors">
                İptal
              </button>
              <button type="submit" disabled={saving} className="flex-1 bg-amber-500 text-black font-bold py-4 rounded-xl hover:bg-amber-600 disabled:opacity-60 transition-colors shadow-lg shadow-amber-500/20" data-testid="movie-save-btn">
                {saving ? 'Kaydediliyor…' : 'Kaydet ve Yayınla'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ---------------- Add Series Modal ---------------- */

function AddSeriesModal({ categories, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_SERIES);
  const [episode, setEpisode] = useState({ season: 1, episode: 1, title: '', stream_url: '' });
  const [saving, setSaving] = useState(false);
  const [tmdbBusy, setTmdbBusy] = useState(false);
  const [tmdbErr, setTmdbErr] = useState('');

  const fetchFromTmdb = async () => {
    if (!form.tmdb_id) { setTmdbErr('Önce TMDB ID girin.'); return; }
    setTmdbErr('');
    setTmdbBusy(true);
    try {
      const res = await api.get(`/tmdb/tv/${encodeURIComponent(form.tmdb_id)}`);
      const d = res.data;
      setForm(f => ({
        ...f,
        title:       f.title || d.title || '',
        year:        d.year ? String(d.year) : f.year,
        description: d.description || f.description,
        poster_url:  d.poster_url || f.poster_url,
      }));
    } catch (err) {
      setTmdbErr(err?.response?.data?.detail || 'TMDB lookup başarısız.');
    } finally {
      setTmdbBusy(false);
    }
  };

  const addEpisode = () => {
    if (!episode.title || !episode.stream_url) return;
    setForm(f => ({ ...f, episodes: [...f.episodes, { ...episode }] }));
    setEpisode(e => ({ season: e.season, episode: e.episode + 1, title: '', stream_url: '' }));
  };

  const removeEp = (idx) => {
    setForm(f => ({ ...f, episodes: f.episodes.filter((_, i) => i !== idx) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/series', {
        ...form,
        year: form.year ? parseInt(form.year) : null,
      });
      onCreated();
      onClose();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Dizi eklenemedi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100] p-4" data-testid="vod-add-modal">
      <div className="bg-slate-900 border border-slate-800 w-full max-w-2xl rounded-2xl p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-white flex items-center gap-2">
            <Tv2 className="text-amber-500" /> Yeni Dizi
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        {categories.length === 0 ? (
          <div className="bg-amber-500/5 border border-amber-700/30 rounded-xl p-5 text-sm text-amber-300/80">
            <strong className="text-amber-400 block mb-1">Kategori yok</strong>
            Dizi eklemek için önce <span className="text-amber-500">/admin/categories</span> sayfasından
            <span className="font-bold"> type: series</span> tipinde bir kategori oluşturun.
          </div>
        ) : (
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-500 uppercase ml-1">Dizi Adı</label>
              <input required placeholder="Örn: Breaking Bad" className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none"
                value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="series-title-input" />
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 uppercase ml-1">Kategori</label>
              <select required className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none cursor-pointer"
                value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} data-testid="series-category-select">
                <option value="">Seçiniz...</option>
                {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 uppercase ml-1">Yıl</label>
              <input type="number" min="1900" max="2099" placeholder="2008" className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono"
                value={form.year} onChange={e => setForm({ ...form, year: e.target.value })} />
            </div>

            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-500 uppercase ml-1">Poster URL</label>
              <input placeholder="https://image.tmdb.org/..." className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono"
                value={form.poster_url} onChange={e => setForm({ ...form, poster_url: e.target.value })} data-testid="series-poster-input" />
            </div>

            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-500 uppercase ml-1">TMDB ID (Opsiyonel)</label>
              <div className="mt-1 flex gap-2">
                <input
                  placeholder="1396"
                  className="flex-1 bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono"
                  value={form.tmdb_id}
                  onChange={e => setForm({ ...form, tmdb_id: e.target.value })}
                  data-testid="series-tmdb-input"
                />
                <button
                  type="button"
                  onClick={fetchFromTmdb}
                  disabled={tmdbBusy || !form.tmdb_id}
                  className="bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-400 px-4 rounded-xl font-bold text-xs uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1"
                  data-testid="series-tmdb-fetch-btn"
                  title="TMDB'den poster, açıklama ve yıl bilgilerini getir"
                >
                  {tmdbBusy ? '⏳' : '↓'} TMDB'den Getir
                </button>
              </div>
              {tmdbErr && <div className="text-[10px] text-red-400 mt-1 ml-1" data-testid="series-tmdb-err">{tmdbErr}</div>}
            </div>

            <div className="md:col-span-2 border-t border-slate-800 pt-4 mt-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-gray-500 uppercase ml-1">
                  Bölümler (<span className="text-amber-400">{form.episodes.length}</span>)
                </span>
              </div>
              <div className="grid grid-cols-[80px,80px,1fr,auto] gap-2 mb-3">
                <input type="number" min="1" placeholder="Sezon" className="bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-white focus:border-amber-500 outline-none font-mono text-sm"
                  value={episode.season} onChange={e => setEpisode({ ...episode, season: parseInt(e.target.value) || 1 })} />
                <input type="number" min="1" placeholder="Bölüm" className="bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-white focus:border-amber-500 outline-none font-mono text-sm"
                  value={episode.episode} onChange={e => setEpisode({ ...episode, episode: parseInt(e.target.value) || 1 })} />
                <input placeholder="Bölüm başlığı" className="bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-white focus:border-amber-500 outline-none text-sm"
                  value={episode.title} onChange={e => setEpisode({ ...episode, title: e.target.value })} data-testid="episode-title-input" />
                <button type="button" onClick={addEpisode} disabled={!episode.title || !episode.stream_url} className="bg-amber-500 text-black px-3 rounded-xl hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed font-bold flex items-center gap-1 text-xs" data-testid="episode-add-btn">
                  <PlusIcon size={14} /> Ekle
                </button>
              </div>
              <input placeholder="Bölüm Stream URL" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-white focus:border-amber-500 outline-none font-mono text-sm mb-3"
                value={episode.stream_url} onChange={e => setEpisode({ ...episode, stream_url: e.target.value })} data-testid="episode-stream-input" />

              {form.episodes.length > 0 && (
                <div className="bg-slate-950 border border-slate-800 rounded-xl divide-y divide-slate-800/60 max-h-[200px] overflow-y-auto" data-testid="episode-list">
                  {form.episodes.map((ep, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2.5 text-xs">
                      <span className="font-mono text-amber-400 shrink-0">S{ep.season}E{ep.episode}</span>
                      <span className="flex-1 text-white truncate">{ep.title}</span>
                      <button type="button" onClick={() => removeEp(idx)} className="text-gray-500 hover:text-red-500 p-1">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-4 pt-4 md:col-span-2">
              <button type="button" onClick={onClose} className="flex-1 bg-slate-800 text-white font-bold py-4 rounded-xl hover:bg-slate-700 transition-colors">
                İptal
              </button>
              <button type="submit" disabled={saving} className="flex-1 bg-amber-500 text-black font-bold py-4 rounded-xl hover:bg-amber-600 disabled:opacity-60 transition-colors shadow-lg shadow-amber-500/20" data-testid="series-save-btn">
                {saving ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}


/* ---------------- Edit Media Modal (works for both movies & series) ---------------- */

function EditMediaModal({ kind, data, categories, onClose, onSaved }) {
  const isMovie = kind === 'movies';
  const [form, setForm] = useState({
    title:        data.title || '',
    category_id:  data.category_id || '',
    poster_url:   data.poster_url || '',
    year:         data.year ?? '',
    tmdb_id:      data.tmdb_id || '',
    description:  data.description || '',
    // movie-only
    stream_url:   data.stream_url || '',
    duration_min: data.duration_min ?? '',
    // series-only
    episodes:     Array.isArray(data.episodes) ? [...data.episodes] : [],
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [ep, setEp] = useState({ season: 1, episode: 1, title: '', stream_url: '' });

  const addEpisode = () => {
    if (!ep.title || !ep.stream_url) return;
    setForm(f => ({ ...f, episodes: [...f.episodes, { ...ep }] }));
    setEp(p => ({ season: p.season, episode: p.episode + 1, title: '', stream_url: '' }));
  };
  const removeEp = (idx) => setForm(f => ({ ...f, episodes: f.episodes.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    const endpoint = isMovie ? 'movies' : 'series';
    const patch = {
      title:       form.title,
      category_id: form.category_id,
      poster_url:  form.poster_url,
      year:        form.year ? parseInt(form.year) : null,
      tmdb_id:     form.tmdb_id,
      description: form.description,
    };
    if (isMovie) {
      patch.stream_url   = form.stream_url;
      patch.duration_min = form.duration_min ? parseInt(form.duration_min) : null;
    } else {
      patch.episodes = form.episodes;
    }
    try {
      const res = await api.put(`/${endpoint}/${data._id}`, patch);
      onSaved(res.data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Güncelleme başarısız.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100] p-4 overflow-y-auto" data-testid="vod-edit-modal">
      <div className="bg-slate-900 border border-amber-500/30 w-full max-w-2xl rounded-2xl p-7 shadow-2xl shadow-amber-500/10 max-h-[90vh] overflow-y-auto my-auto">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-2xl font-bold text-white flex items-center gap-2">
            <Pencil className="text-amber-500" /> {isMovie ? 'Filmi Düzenle' : 'Diziyi Düzenle'}
          </h3>
          <button onClick={onClose} type="button" className="text-gray-400 hover:text-white" data-testid="vod-edit-close">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-xs font-bold text-gray-500 uppercase ml-1">Başlık</label>
            <input required className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none"
                   value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="vod-edit-title" />
          </div>

          <div>
            <label className="text-xs font-bold text-gray-500 uppercase ml-1">Kategori</label>
            <select className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none cursor-pointer"
                    value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} data-testid="vod-edit-category">
              <option value="">Kategorisiz</option>
              {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-bold text-gray-500 uppercase ml-1">Yıl</label>
            <input type="number" min="1900" max="2099" className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono"
                   value={form.year} onChange={e => setForm({ ...form, year: e.target.value })} data-testid="vod-edit-year" />
          </div>

          {isMovie && (
            <>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase ml-1">Süre (dk)</label>
                <input type="number" min="1" className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono"
                       value={form.duration_min} onChange={e => setForm({ ...form, duration_min: e.target.value })} data-testid="vod-edit-duration" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase ml-1">TMDB ID</label>
                <input className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono"
                       value={form.tmdb_id} onChange={e => setForm({ ...form, tmdb_id: e.target.value })} data-testid="vod-edit-tmdb" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-gray-500 uppercase ml-1">Stream URL</label>
                <input required className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono"
                       value={form.stream_url} onChange={e => setForm({ ...form, stream_url: e.target.value })} data-testid="vod-edit-stream" />
              </div>
            </>
          )}

          {!isMovie && (
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-500 uppercase ml-1">TMDB ID</label>
              <input className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono"
                     value={form.tmdb_id} onChange={e => setForm({ ...form, tmdb_id: e.target.value })} data-testid="vod-edit-tmdb" />
            </div>
          )}

          <div className="md:col-span-2">
            <label className="text-xs font-bold text-gray-500 uppercase ml-1">Poster URL</label>
            <input className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono"
                   value={form.poster_url} onChange={e => setForm({ ...form, poster_url: e.target.value })} data-testid="vod-edit-poster" />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-bold text-gray-500 uppercase ml-1">Açıklama</label>
            <textarea rows="3" className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none resize-none"
                      value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="vod-edit-description" />
          </div>

          {!isMovie && (
            <div className="md:col-span-2 border-t border-slate-800 pt-4 mt-2">
              <div className="text-xs font-bold text-gray-500 uppercase ml-1 mb-2">
                Bölümler (<span className="text-amber-400">{form.episodes.length}</span>)
              </div>
              <div className="grid grid-cols-[80px,80px,1fr,auto] gap-2 mb-3">
                <input type="number" min="1" placeholder="S" className="bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-white focus:border-amber-500 outline-none font-mono text-sm"
                       value={ep.season} onChange={e => setEp({ ...ep, season: parseInt(e.target.value) || 1 })} />
                <input type="number" min="1" placeholder="E" className="bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-white focus:border-amber-500 outline-none font-mono text-sm"
                       value={ep.episode} onChange={e => setEp({ ...ep, episode: parseInt(e.target.value) || 1 })} />
                <input placeholder="Bölüm başlığı" className="bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-white focus:border-amber-500 outline-none text-sm"
                       value={ep.title} onChange={e => setEp({ ...ep, title: e.target.value })} data-testid="vod-edit-episode-title" />
                <button type="button" onClick={addEpisode} disabled={!ep.title || !ep.stream_url} className="bg-amber-500 text-black px-3 rounded-xl hover:bg-amber-600 disabled:opacity-40 font-bold flex items-center gap-1 text-xs">
                  <PlusIcon size={14} /> Ekle
                </button>
              </div>
              <input placeholder="Bölüm Stream URL" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-white focus:border-amber-500 outline-none font-mono text-sm mb-3"
                     value={ep.stream_url} onChange={e => setEp({ ...ep, stream_url: e.target.value })} data-testid="vod-edit-episode-stream" />
              {form.episodes.length > 0 && (
                <div className="bg-slate-950 border border-slate-800 rounded-xl divide-y divide-slate-800/60 max-h-[200px] overflow-y-auto">
                  {form.episodes.map((e2, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2.5 text-xs">
                      <span className="font-mono text-amber-400 shrink-0">S{e2.season}E{e2.episode}</span>
                      <span className="flex-1 text-white truncate">{e2.title}</span>
                      <button type="button" onClick={() => removeEp(idx)} className="text-gray-500 hover:text-red-500 p-1">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {err && (
            <div className="md:col-span-2 flex items-start gap-2 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-xs" data-testid="vod-edit-error">
              <X size={14} className="shrink-0 mt-0.5" /> <span>{err}</span>
            </div>
          )}

          <div className="flex gap-3 pt-4 md:col-span-2">
            <button type="button" onClick={onClose} className="flex-1 bg-slate-800 text-white font-bold py-3 rounded-xl hover:bg-slate-700 transition-colors" data-testid="vod-edit-cancel">
              İptal
            </button>
            <button type="submit" disabled={saving} className="flex-1 bg-amber-500 text-black font-bold py-3 rounded-xl hover:bg-amber-600 disabled:opacity-60 transition-colors shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2" data-testid="vod-edit-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {saving ? 'Kaydediliyor…' : 'Değişiklikleri Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
